import {noop} from 'lodash';

import platformConfig from '/config/platform.config';
import {
  CONTACT_ACTION_TYPES,
  EVENT_NAMES,
  USER_TYPES,
} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {copyToClipboard} from '/util/clipboard.util';
import {generateCallback} from '/util/component.util';
import {getTealiumUtilDefaultInstance} from '/util/tealium';

import {changeNum} from './ProfileContactSection.util';

interface useProfileContactSectionProps {
  onPress: (params: {item: any; type: any}) => void;
  phoneNumber: string;
  accountEmail: string;
  candidateId: string;
  lastPaidRank: string;
}

export function useProfileContactSection({
  onPress,
  phoneNumber,
  accountEmail,
  candidateId,
  lastPaidRank,
}: useProfileContactSectionProps) {
  const {ts} = useTranslation('profileContactSection.actionButtons');
  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.contactClick,
  });
  const generateOnContactPressCall = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.candidate,
      userId: candidateId,
      contact_method: CONTACT_ACTION_TYPES.call,
      lastPaidRank: lastPaidRank,
    },
  });
  const generateOnContactPressEmail = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.candidate,
      userId: candidateId,
      contact_method: CONTACT_ACTION_TYPES.email,
    },
  });
  const generateOnContactPressText = generateCallback({
    func: sendEvent,
    params: {
      userType: USER_TYPES.candidate,
      userId: candidateId,
      contact_method: CONTACT_ACTION_TYPES.text,
    },
  });
  const combineCallbacksCall = (item: any, type: any) => {
    return () => {
      generateOnContactPressCall();
      onPress({item, type});
    };
  };

  const combineCallbacksText = (item: any, type: any) => {
    return () => {
      generateOnContactPressText();
      onPress({item, type});
    };
  };
  const combineCallbacksEmail = (item: any, type: any) => {
    return () => {
      onPress({item, type});
      generateOnContactPressEmail();
    };
  };
  let phoneActionButtons = [
    {
      text: ts('call', 'Call') + ` ${changeNum(phoneNumber)}`,
      onPress: candidateId
        ? generateCallback({
            func: combineCallbacksCall(phoneNumber, 'tel'),
          })
        : generateCallback({
            func: onPress,
            params: {item: phoneNumber, type: 'tel'},
          }),
    },
    {
      text: ts('text', 'Text') + ` ${changeNum(phoneNumber)}`,
      onPress: candidateId
        ? generateCallback({
            func: combineCallbacksText(phoneNumber, 'sms'),
          })
        : generateCallback({
            func: onPress,
            params: {item: phoneNumber, type: 'sms'},
          }),
    },
    {
      text: ts('copy', 'Copy') + ` "${changeNum(phoneNumber)}"`,
      onPress: generateCallback({
        func: copyToClipboard,
        params: phoneNumber,
      }),
    },
  ];

  let emailActionButtons = [
    {
      text: ts('email', 'Email') + ` ${accountEmail}`,
      onPress: candidateId
        ? generateCallback({
            func: combineCallbacksEmail(accountEmail, 'mail'),
          })
        : generateCallback({
            func: onPress,
            params: {item: accountEmail, type: 'mail'},
          }),
    },
    {
      text: ts('copy', 'Copy') + ` "${accountEmail}"`,
      onPress: generateCallback({
        func: copyToClipboard,
        params: accountEmail,
      }),
    },
  ];

  if (platformConfig.getOS() === 'ios') {
    phoneActionButtons.push({
      text: ts('cancel', 'Cancel'),
      onPress: noop,
      // @ts-expect-error TS(2345): Argument of type '{ text: any; onPress: any; style... Remove this comment to see the full error message
      style: 'cancel',
    });
    emailActionButtons.push({
      text: ts('cancel', 'Cancel'),
      onPress: noop,
      // @ts-expect-error TS(2345): Argument of type '{ text: any; onPress: any; style... Remove this comment to see the full error message
      style: 'cancel',
    });
  }

  return {phoneActionButtons, emailActionButtons};
}
